import {
    createAction,
    createListenerMiddleware,
    createSlice,
    createSelector,
    TypedStartListening,
} from '@reduxjs/toolkit';
import Notifications from 'react-notification-system-redux';
import { stopSubmit } from 'redux-form';
import * as actions from '../actions';
import { ApplicationDispatch, ApplicationState } from '@/store';
import { convertErrorToFormError } from '@/utils';
import { getTranslate } from 'react-localize-redux';

type InitialState = {
    retentionModal: boolean;
    modal: boolean;
    success: boolean;
    skip: boolean;
    isSubmitting: boolean;
    data: {
        [key: string]: any;
    };
};

const initialState: InitialState = {
    retentionModal: false,
    modal: false,
    success: false,
    skip: false,
    isSubmitting: false,
    data: {},
};

export type FormStartListening = TypedStartListening<ApplicationState, ApplicationDispatch>;

export const skip = createAction('cancelLicenseQuestionaire/skip');
export const reset = createAction('cancelLicenseQuestionaire/reset');

export const questionaireFilled = createSelector(
    (s: ApplicationState) => s.cancelLicenseQuestionaire,
    (s) => Object.keys(s.data).length > 0
);

export const formsListener = createListenerMiddleware();
const startFormsListening = formsListener.startListening as FormStartListening;

const cancelLicenseQuestionaireSlice = createSlice({
    name: 'cancelLicenseQuestionaire',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(actions.cancelRetentionModalOpen, (state) => {
            state.retentionModal = true;
        });

        builder.addCase(actions.cancelRetentionModalClose, (state) => {
            state.retentionModal = false;
        });

        builder.addCase(actions.cancelRetentionModalProceed, (state) => {
            state.retentionModal = false;
            state.modal = true;
        });

        builder.addCase(actions.cancelQuestionaireOpenModal, (state) => {
            state.modal = true;
        });

        builder.addCase(actions.cancelQuestionaireCloseModal, (state) => {
            state.modal = false;
            state.retentionModal = false;
            state.skip = false;
            state.data = {};
        });

        builder.addCase(actions.cancelQuestionaireProceed, (state, action) => {
            state.modal = false;
            state.data = action.payload ?? {};
        });

        builder.addCase(skip, (state) => {
            state.modal = false;
            state.skip = true;
        });

        builder.addCase(reset, () => initialState);

        builder.addCase(actions.CANCEL_ALL_LICENSES.REQUEST, (state) => {
            state.isSubmitting = true;
            state.success = false;
        });

        builder.addCase(actions.CANCEL_ALL_LICENSES.SUCCESS, (state) => {
            state.isSubmitting = false;
            state.success = true;
        });

        builder.addCase(actions.CANCEL_ALL_LICENSES.FAILURE, (state) => {
            state.isSubmitting = false;
        });
    },
});

startFormsListening({
    predicate: (action) => action.type === actions.sendCancelQuestionaire.REQUEST,
    effect: async ({ payload }: { [key: string]: any }, api) => {
        const formFields = Object.entries(payload).filter(([key]) => {
            return !key.includes('main') && !key.includes('comments');
        });

        if (formFields.length < 1) {
            const translate = getTranslate(api.getState().localize);
            const error = new Error(
                translate('cancelLicenseQuestionaireModal.selectOptionFromEachSection') as string
            );
            api.dispatch(stopSubmit('cancelLicenseQuestionaire', convertErrorToFormError(error)));
            return;
        }

        api.dispatch(actions.cancelQuestionaireProceed(payload));
    },
});

startFormsListening({
    predicate: (action) => action.type === actions.CANCEL_ALL_LICENSES.FAILURE,
    effect: async (action, api) => {
        if (api.getState().cancelLicenseQuestionaire.skip) {
            api.dispatch(
                Notifications.error({
                    // @ts-ignore
                    title: action.payload.response.data.ResponseStatus.Message,
                })
            );
        }
    },
});

export default cancelLicenseQuestionaireSlice;
